import { makeFormData } from "utils/makeFormData";
import { ChatQnAResponse } from "types/Models/chatQnA";
import { RequestedAnswerReviewDecision } from "constants/requestedAnswerReview";
import { emptyApi } from "./emptyApi";

const UPDATE_CHAT_QNA_ANSWER_CACHE_KEY_PREFIX = "updateChatQnaAnswer_";

export const getUpdateChatQNAAnswerCacheKey = (requestId: string): string =>
  `${UPDATE_CHAT_QNA_ANSWER_CACHE_KEY_PREFIX}${requestId}`;

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getChatQNA: build.query<
      ChatQnAResponse,
      { requestId: string; groupId: string }
    >({
      query: ({ requestId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/?answer_request_id=${requestId}`,
        };
      },
      providesTags: ["CHAT_QNA"],
    }),
    createChatQNA: build.mutation<
      ChatQnAResponse,
      {
        groupId: string;
        channelId: string;
        requestData: {
          question: string;
          sequence: number;
          file?: File;
          fileId?: string;
          documentIndex?: number | null;
          xPosition?: number | null;
          yPosition?: number | null;
          pageNumber?: number | null;
        }[];
        referenceDocs?: { file?: File; fileId?: string; docIndex: number }[];
      }
    >({
      query: ({ groupId, channelId, requestData, referenceDocs = [] }) => {
        const requestDataPayload = requestData.map(
          ({
            question,
            sequence,
            documentIndex,
            xPosition,
            yPosition,
            pageNumber,
          }) => ({
            question,
            sequence,
            ...(documentIndex != null && { document_index: documentIndex }),
            ...(xPosition != null && { x_position: xPosition }),
            ...(yPosition != null && { y_position: yPosition }),
            ...(pageNumber != null && { page_number: pageNumber }),
          })
        );

        type FilesMetadataEntry =
          | { index: number; file_id?: string }
          | { index: number; key: string; file_id?: null };
        const filesMetadata: FilesMetadataEntry[] = [];
        const filesPayload: Record<string, File> = {};

        requestData.forEach((item, index) => {
          if (!item.file && !item.fileId) return;

          if (item.fileId) {
            filesMetadata.push({ index, file_id: item.fileId });
            return;
          }

          const key = `file_${filesMetadata.length}`;
          filesPayload[key] = item.file!;
          filesMetadata.push({ key, index, file_id: null });
        });

        const referenceDocsMetadata = referenceDocs.map((doc) =>
          doc.file
            ? { key: `reference_doc_${doc.docIndex}`, doc_index: doc.docIndex }
            : { file_id: doc.fileId, doc_index: doc.docIndex }
        );

        const referenceDocFiles: Record<string, File> = {};
        referenceDocs.forEach((doc) => {
          if (doc.file) {
            referenceDocFiles[`reference_doc_${doc.docIndex}`] = doc.file;
          }
        });

        const payload: Record<string, string | File> = {
          channel_id: channelId,
          request_data: JSON.stringify(requestDataPayload),
          ...filesPayload,
          ...referenceDocFiles,
        };

        if (filesMetadata.length > 0) {
          payload.files_json_metadata = JSON.stringify(filesMetadata);
        }

        if (referenceDocsMetadata.length > 0) {
          payload.reference_docs_metadata = JSON.stringify(
            referenceDocsMetadata
          );
        }

        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/`,
          method: "post",
          body: makeFormData(payload),
        };
      },
      invalidatesTags: ["CHAT_QNA", "ALL_ACTION_ITEMS", "Tasks"],
    }),
    updateChatQNA: build.mutation<
      ChatQnAResponse,
      {
        answerRequestId: string;
        requestedAnswer: string | null;
        requestId: string;
        groupId: string;
      }
    >({
      query: ({ answerRequestId, requestedAnswer, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/${answerRequestId}/`,
          method: "put",
          body: {
            requested_answer: requestedAnswer,
          },
        };
      },
      onQueryStarted: async (
        { answerRequestId, requestedAnswer, requestId, groupId },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "getChatQNA",
            { requestId, groupId },
            (draft) => {
              const requestedAnswerEntry = draft.requested_answers.find(
                ({ uuid }) => uuid === answerRequestId
              );
              if (requestedAnswerEntry) {
                requestedAnswerEntry.answer = requestedAnswer;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["CHAT_QNA", "ALL_ACTION_ITEMS"],
    }),
    submitChatQNA: build.mutation<
      ChatQnAResponse,
      { requestId: string; groupId: string }
    >({
      query: ({ requestId, groupId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/${requestId}/submit/`,
          method: "post",
        };
      },
      invalidatesTags: ["CHAT_QNA", "ALL_ACTION_ITEMS", "Tasks"],
    }),
    reviewChatQNA: build.mutation<
      ChatQnAResponse,
      {
        requestId: string;
        groupId: string;
        reviews: {
          uuid: string;
          decision: RequestedAnswerReviewDecision;
          followUpNote?: string;
        }[];
      }
    >({
      query: ({ requestId, groupId, reviews }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/${requestId}/review/`,
          method: "post",
          body: {
            reviews: reviews.map(({ uuid, decision, followUpNote }) => ({
              uuid,
              decision,
              ...(followUpNote != null && { follow_up_note: followUpNote }),
            })),
          },
        };
      },
      invalidatesTags: ["CHAT_QNA", "ALL_ACTION_ITEMS", "Tasks"],
    }),
    saveReviewProgress: build.mutation<
      ChatQnAResponse,
      {
        requestedAnswerId: string;
        groupId: string;
        decision: RequestedAnswerReviewDecision;
        followUpNote?: string;
      }
    >({
      query: ({ requestedAnswerId, groupId, decision, followUpNote }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/${requestedAnswerId}/review-progress/`,
          method: "put",
          body: {
            decision,
            ...(followUpNote != null && { follow_up_note: followUpNote }),
          },
        };
      },
      invalidatesTags: ["CHAT_QNA"],
    }),
    deleteChatQNADocument: build.mutation<
      ChatQnAResponse,
      { groupId: string; documentId: string }
    >({
      query: ({ groupId, documentId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/answer-request/document/${documentId}/`,
          method: "delete",
        };
      },
      invalidatesTags: ["CHAT_QNA"],
    }),
    uploadChatQNADocument: build.mutation<
      ChatQnAResponse,
      | { groupId: string; requestedAnswerId: string; file: File }
      | { groupId: string; requestedAnswerId: string; fileId: string }
    >({
      query: (args) => {
        let body: FormData | { file_id: string };
        if ("file" in args) {
          const formData = new FormData();
          formData.append("file", args.file);
          body = formData;
        } else {
          body = { file_id: args.fileId };
        }

        return {
          url: `/api/inkle/messaging/group/${args.groupId}/answer-request/${args.requestedAnswerId}/document/`,
          method: "post",
          body,
        };
      },
      invalidatesTags: ["CHAT_QNA"],
    }),
  }),
});

export const {
  useCreateChatQNAMutation,
  useGetChatQNAQuery,
  useUpdateChatQNAMutation,
  useSubmitChatQNAMutation,
  useReviewChatQNAMutation,
  useSaveReviewProgressMutation,
  useUploadChatQNADocumentMutation,
  useDeleteChatQNADocumentMutation,
} = extendedApi;
